import { useEffect, useRef, useState } from 'react';

interface AddressSearchProps {
  onClose: () => void;
  onSelectAddress: (roadAddress: string, jibunAddress: string) => void;
}

// Daum Postcode 타입 선언
declare global {
  interface Window {
    daum: any;
  }
}

const AddressSearch = ({ onClose, onSelectAddress }: AddressSearchProps) => {
  const postcodeRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    // 스크립트 로드를 기다리는 함수
    const waitForDaumPostcode = () => {
      return new Promise((resolve, reject) => {
        // 이미 로드되어 있는지 먼저 확인
        if (window.daum && window.daum.Postcode) {
          resolve(true);
          return;
        }

        let attempts = 0;
        const maxAttempts = 50; // 5초 (100ms * 50)

        const checkInterval = setInterval(() => {
          attempts++;

          if (window.daum && window.daum.Postcode) {
            clearInterval(checkInterval);
            resolve(true);
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            reject(new Error('Daum Postcode 스크립트 로드 실패. 페이지를 새로고침 해주세요.'));
          }
        }, 100);
      });
    };

    // Daum 우편번호 서비스 초기화
    const initPostcode = async () => {
      try {
        // 스크립트 로드 대기
        await waitForDaumPostcode();

        // 컴포넌트가 언마운트되었는지 확인
        if (!isMounted) return;

        // DOM 렌더링 완료를 위한 짧은 지연
        await new Promise(resolve => setTimeout(resolve, 100));

        if (!isMounted || !postcodeRef.current) {
          console.error('Component unmounted or postcodeRef.current is null');
          return;
        }

        console.log('Initializing Daum Postcode...');

        // 기존 내용 정리
        postcodeRef.current.innerHTML = '';

        // Daum 우편번호 서비스 생성
        const postcode = new window.daum.Postcode({
          oncomplete: function(data: any) {
            // 도로명 주소와 지번 주소 추출
            const roadAddress = data.roadAddress || data.address;
            const jibunAddress = data.jibunAddress || data.autoJibunAddress || data.address;

            // 부모 컴포넌트로 주소 전달
            onSelectAddress(roadAddress, jibunAddress);
            onClose();
          },
          onresize: function(size: any) {
            if (postcodeRef.current) {
              postcodeRef.current.style.height = size.height + 'px';
            }
          },
          width: '100%',
          height: '100%'
        });

        console.log('Embedding Daum Postcode...');
        // 우편번호 검색창 삽입
        postcode.embed(postcodeRef.current);
        console.log('Daum Postcode initialized successfully');

        if (isMounted) {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Daum Postcode 초기화 실패:', err);
        if (isMounted) {
          setError('주소 검색 서비스를 불러오는데 실패했습니다.');
          setIsLoading(false);
        }
      }
    };

    initPostcode();

    // Cleanup 함수
    return () => {
      isMounted = false;
      if (postcodeRef.current) {
        postcodeRef.current.innerHTML = '';
      }
    };
  }, [onSelectAddress, onClose]);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'white'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px' }}>주소 검색</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px'
        }}>
          {isLoading && (
            <div style={{ textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>주소 검색 서비스 로딩 중...</div>
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', color: '#d32f2f', padding: '20px' }}>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>{error}</div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                페이지를 새로고침하거나, 잠시 후 다시 시도해주세요.
              </div>
            </div>
          )}

          {/* Daum Postcode Container */}
          <div
            ref={postcodeRef}
            style={{
              width: '100%',
              height: '100%',
              minHeight: '400px',
              display: isLoading || error ? 'none' : 'block'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AddressSearch;
